/**
 * Prediction UI (P3-04). Estimates are always labelled as estimates, shown as
 * ranges with sample sizes, kept off the timeline and accompanied by the
 * caregiver-cues safety copy. No confidence percentages are shown.
 */
import { useEffect, useMemo, useRef } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { useApp, useNow } from '@/app/AppContext';
import { db } from '@/db/db';
import type { LocalEvent } from '@/lib/schemas';
import { formatDateTime, formatDuration, formatTime, localDayKey } from '@/lib/time';
import { computeFeedingPrediction, type FeedingPrediction } from './feeding';
import { computeSleepPrediction, type SleepPrediction } from './sleep';
import { backtestFeeding, backtestSleepStart, backtestWake, type Evaluation } from './evaluate';
import { logPrediction, resolvePredictionLogs, summarizeLogs } from './log';

export const SAFETY_COPY = "Estimates only. Follow your baby's cues and professional medical advice; do not delay feeding because of a prediction.";
const OVERDUE_COPY = 'Later than the usual range. Follow your baby’s cues.';

function usePredictions(events: LocalEvent[]) {
  const { tz } = useApp();
  const now = useNow(60_000);
  const feeding = useMemo(() => computeFeedingPrediction(events, tz, now), [events, tz, now]);
  const sleep = useMemo(() => computeSleepPrediction(events, tz, now), [events, tz, now]);
  return { feeding, sleep, now, tz };
}

function when(ms: number, nowMs: number, tz: string): string {
  return localDayKey(ms, tz) === localDayKey(nowMs, tz) ? formatTime(ms, tz) : formatDateTime(ms, tz);
}

function range(startMs: number, endMs: number, nowMs: number, tz: string): string {
  return `${when(startMs, nowMs, tz)} – ${when(endMs, nowMs, tz)}`;
}

function minutes(m: number): string {
  return formatDuration(m * 60_000);
}

function FeedingCard({ prediction, nowMs, tz }: { prediction: FeedingPrediction; nowMs: number; tz: string }) {
  return <section className="card space-y-2 border border-dashed border-feed/40 p-4" aria-label="Next feed estimate">
    <p className="eyebrow text-feed">Estimate · next feed</p>
    {prediction.status === 'ok' ? <>
      <p className="font-display text-xl font-semibold num">Likely {range(prediction.window.startMs, prediction.window.endMs, nowMs, tz)}</p>
      <p className="text-sm text-ink-300">Around {when(prediction.window.pointMs, nowMs, tz)}, about {minutes(prediction.medianMin)} after the last feed. Based on {prediction.sampleSize} recent feed intervals{prediction.group === 'all' ? '' : ` at ${prediction.group === 'night' ? 'night' : 'daytime'}`}.</p>
      {prediction.widened && <p className="text-xs text-ink-300">The range is wider because recent records are limited or vary a lot.</p>}
      {prediction.overdue && <p className="text-sm text-warn">{OVERDUE_COPY}</p>}
    </> : <>
      <p className="font-semibold">Not enough data yet</p>
      <p className="text-sm text-ink-300">{prediction.reason} Estimates appear once there are enough regular feeds from the last three weeks.</p>
    </>}
  </section>;
}

function SleepCard({ prediction, nowMs, tz }: { prediction: SleepPrediction; nowMs: number; tz: string }) {
  return <section className="card space-y-2 border border-dashed border-sleep/40 p-4" aria-label="Sleep estimate">
    <p className="eyebrow text-sleep">Estimate · sleep <span className="ml-1 rounded-full border border-sleep/40 px-2 py-0.5 text-[0.65rem] normal-case tracking-normal">Experimental</span></p>
    {prediction.status === 'awake' && <>
      <p className="font-display text-xl font-semibold num">Next sleep likely {range(prediction.window.startMs, prediction.window.endMs, nowMs, tz)}</p>
      <p className="text-sm text-ink-300">Based on {prediction.sampleSize} recent awake periods.{prediction.duration ? ` It may last ${minutes(prediction.duration.lowerMin)} – ${minutes(prediction.duration.upperMin)} (from ${prediction.duration.sampleSize} sleeps).` : ''}</p>
    </>}
    {prediction.status === 'sleeping' && <>
      <p className="font-display text-xl font-semibold num">May wake {range(prediction.window.startMs, prediction.window.endMs, nowMs, tz)}</p>
      <p className="text-sm text-ink-300">Based on {prediction.sampleSize} similar completed sleeps. The current sleep is not used until it ends.</p>
    </>}
    {prediction.status !== 'insufficient' && prediction.overdue && <p className="text-sm text-warn">{OVERDUE_COPY}</p>}
    {prediction.status === 'insufficient' && <>
      <p className="font-semibold">Not enough data yet</p>
      <p className="text-sm text-ink-300">{prediction.reason}</p>
    </>}
    <p className="text-xs text-ink-300">Sleep estimates are experimental until more records show they are reliable.</p>
  </section>;
}

/**
 * Compact estimates for Home. Also records each new prediction once in the
 * local prediction log and fills in outcomes as events arrive (local
 * households only; failures never affect tracking).
 */
export function HomePredictions({ events }: { events: LocalEvent[] }) {
  const { baby } = useApp();
  const { feeding, sleep, now, tz } = usePredictions(events);
  const logged = useRef(new Set<string>());
  useEffect(() => {
    if (!baby) return;
    const items: Array<[string, Parameters<typeof logPrediction>[1]]> = [];
    if (feeding.status === 'ok') items.push([`feed|${feeding.basisEventId}`, feeding]);
    if (sleep.status !== 'insufficient') items.push([`${sleep.status}|${sleep.basisEventId}`, sleep]);
    for (const [kind, p] of items) {
      const key = `${baby.id}|${kind}`;
      if (logged.current.has(key)) continue;
      logged.current.add(key);
      void logPrediction(baby.id, p).catch(() => logged.current.delete(key));
    }
  }, [baby, feeding, sleep]);
  useEffect(() => {
    if (baby) void resolvePredictionLogs(baby.id, events).catch(() => {});
  }, [baby, events]);
  return <section className="space-y-3" aria-label="Estimates">
    <div className="grid gap-3 sm:grid-cols-2">
      <FeedingCard prediction={feeding} nowMs={now} tz={tz} />
      <SleepCard prediction={sleep} nowMs={now} tz={tz} />
    </div>
    <p className="text-xs text-ink-300">{SAFETY_COPY}</p>
  </section>;
}

function pct(x: number | null) { return x === null ? '—' : `${Math.round(x * 100)}%`; }
function mins(x: number | null) { return x === null ? '—' : `${Math.round(x)} min`; }

function AccuracyTable({ rows }: { rows: Array<[string, Evaluation]> }) {
  return <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Estimate accuracy table">
    <table className="w-full text-left text-sm num" style={{ minWidth: '34rem' }}>
      <caption className="sr-only">Accuracy of estimates against your past records, compared with a simple recent-median guess</caption>
      <thead><tr className="text-ink-300">{['Estimate', 'Checked', 'Average error', 'Inside range', 'Simple guess error', 'Simple guess inside range'].map((h) => <th key={h} scope="col" className="p-2 font-normal">{h}</th>)}</tr></thead>
      <tbody>{rows.map(([label, r]) => <tr key={label} className="border-t border-white/5">
        <th scope="row" className="p-2 text-left font-normal">{label}</th>
        <td className="p-2">{r.model.cases}</td><td className="p-2">{mins(r.model.maeMin)}</td><td className="p-2">{pct(r.model.coverage)}</td>
        <td className="p-2">{mins(r.baseline.maeMin)}</td><td className="p-2">{pct(r.baseline.coverage)}</td>
      </tr>)}</tbody>
    </table>
  </div>;
}

/** Insights page: current estimates, how they are made, and how accurate they have been. */
export function Insights({ events }: { events: LocalEvent[] }) {
  const { baby } = useApp();
  const { feeding, sleep, now, tz } = usePredictions(events);
  const evaluation = useMemo<Array<[string, Evaluation]>>(() => [
    ['Next feed', backtestFeeding(events, tz)],
    ['Next sleep (experimental)', backtestSleepStart(events, tz)],
    ['Wake time (experimental)', backtestWake(events, tz)],
  ], [events, tz]);
  const logs = useLiveQuery(
    () => baby ? db.predictionLog.where('[baby_id+prediction_type+generated_ms]').between([baby.id, '', -Infinity], [baby.id, '￿', Infinity]).toArray() : [],
    [baby?.id],
  );
  const live = summarizeLogs(logs ?? []);
  return <>
    <div><p className="eyebrow">Looking ahead</p><h1 className="mt-1 text-3xl font-semibold">Insights</h1></div>
    <p className="rounded-2xl border border-warn/30 bg-warn/10 p-3 text-sm" role="note">{SAFETY_COPY}</p>
    <div className="grid gap-3 sm:grid-cols-2">
      <FeedingCard prediction={feeding} nowMs={now} tz={tz} />
      <SleepCard prediction={sleep} nowMs={now} tz={tz} />
    </div>
    <section className="card space-y-3 p-4" aria-labelledby="accuracy-heading">
      <h2 id="accuracy-heading" className="text-lg font-semibold">How accurate have estimates been?</h2>
      <p className="text-sm text-ink-300">Each past feed or sleep is estimated using only the records before it, then compared with what actually happened. The simple guess uses the middle of the last 10 intervals. A range is a likely window, not a promise.</p>
      <AccuracyTable rows={evaluation} />
      <p className="text-sm">{live.resolved === 0
        ? 'Estimates shown on this device will be checked against your records as new entries arrive.'
        : `Estimates shown on this device: ${live.resolved} checked, average error ${mins(live.maeMin)}, ${pct(live.coverage)} inside the range.`}</p>
    </section>
    <section className="card space-y-2 p-4">
      <h2 className="font-semibold">How estimates work</h2>
      <p className="text-sm text-ink-300">Estimates are calculated on this device from your recorded feeds and sleeps in the last three weeks, giving recent days more weight. Feeds within 10 minutes of each other count as one feed, and gaps longer than 8 hours are treated as untracked time. No data is sent anywhere to make them. Times are shown in {tz}.</p>
    </section>
  </>;
}
